import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@invent/wl-ui-kit-next/ui';
import { cn } from '@invent/wl-ui-kit-next';
import { History, Search } from 'lucide-react';
import { Link } from 'react-router-dom';

import { RefreshButton, SEARCH_EVERYTHING, type IAppRefresh, type IRecordVisit } from '@/design';
import { useCan } from '@/domains/auth';
import { findLabel } from './command-palette';

/**
 * The Team Portal's 48 px header: the command palette's trigger and the recent records (the
 * two-portals design, §4.2).
 *
 * **No cycle or survey scope** (SR-439): the header's two menus looked like a filter over the
 * whole portal and set only People's and Responses' own parameters, at the moment they changed —
 * every other list has its own Cycle and Survey filters and read nothing of them. They promised
 * more than they did, so they went; each list's filters are the one place a cycle is chosen.
 *
 * **A work page's compact bar takes its row** (`compact`, `condense.tsx`): drawn over the header,
 * the header's own controls hidden under it, the palette's trigger a 🔍 in the bar.
 *
 * **The palette's trigger is "Search everything", at the row's end**, in the header and in the bar
 * alike: a work page's own search is a box in the page that filters its list (`FilterBox`), and the
 * two are told apart by place, icon and name.
 */
export function TeamHeader({
  onOpenPalette,
  recent,
  refresh = null,
  menu,
  host,
  compact = false
}: {
  onOpenPalette: () => void;
  recent: IRecordVisit[];
  /** The open page's *Refresh*, left of the search; none on a page that has none. */
  refresh?: Nullable<IAppRefresh>;
  /** The rail's stand-in below 1024 px, where the rail itself is not drawn. */
  menu: React.ReactNode;
  /** Receives the header's element — where a page's compact bar is drawn. */
  host?: (element: Nullable<HTMLElement>) => void;
  /** A compact bar covers the header: its own controls are hidden under it. */
  compact?: boolean;
}) {
  const can = useCan();
  return (
    <header
      ref={host}
      className={cn(
        'sticky top-0 z-20 flex min-h-12 shrink-0 flex-wrap items-center gap-2 border-b border-border bg-white/95 px-4 py-2 backdrop-blur lg:h-12 lg:flex-nowrap lg:py-0',
        '[&>:not([data-compact-bar])]:transition-[opacity,visibility] [&>:not([data-compact-bar])]:duration-150 motion-reduce:[&>:not([data-compact-bar])]:transition-none',
        compact && '[&>:not([data-compact-bar])]:invisible [&>:not([data-compact-bar])]:opacity-0'
      )}
    >
      {menu}

      {refresh && (
        <span className="ml-auto hidden items-center gap-1 lg:inline-flex">
          <RefreshButton reading={refresh.reading} readAt={refresh.readAt} onClick={refresh.onClick} />
        </span>
      )}

      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onOpenPalette}
            className={cn(
              'flex h-8 min-w-0 flex-1 items-center gap-2 lg:w-full',
              refresh ? 'lg:ml-2' : 'lg:ml-auto',
              'rounded-md bg-[color-mix(in_srgb,var(--color-royal-blue)_6%,white)] px-2.5 text-[0.8125rem] text-muted-foreground outline-none hover:bg-[color-mix(in_srgb,var(--color-royal-blue)_9%,white)] focus-visible:ring-2 focus-visible:ring-ring lg:max-w-md lg:flex-none'
            )}
          >
            <Search className="size-4 shrink-0" aria-hidden />
            <span className="truncate">{findLabel(can)}</span>
            <kbd className="ml-auto hidden rounded border border-border bg-white px-1.5 font-sans text-[0.6875rem] leading-[1.1rem] sm:inline">
              Ctrl K
            </kbd>
          </button>
        </TooltipTrigger>
        <TooltipContent>{SEARCH_EVERYTHING}</TooltipContent>
      </Tooltip>

      <RecentMenu recent={recent} />
    </header>
  );
}

/** The records opened last — the row's last control, after the search, in the header and (`compact`,
 *  an icon named "Recent") in a work page's compact bar. */
export function RecentMenu({ recent, compact = false }: { recent: IRecordVisit[]; compact?: boolean }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {compact ? (
          <Button type="button" variant="ghost" size="icon" aria-label="Recent" title="Recent" className="size-8 shrink-0 text-muted-foreground">
            <History className="size-4" aria-hidden />
          </Button>
        ) : (
          <Button type="button" variant="ghost" size="sm" className="shrink-0 gap-1.5 font-normal">
            <History className="size-4" aria-hidden />
            Recent
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          Records you opened last, on this browser
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {recent.length === 0 ? (
          <p className="px-2 py-1.5 text-xs text-muted-foreground">Nothing opened yet.</p>
        ) : (
          recent.map((record) => (
            <DropdownMenuItem key={record.path} asChild>
              <Link to={record.path} className="grid gap-0">
                <span className="truncate">{record.label}</span>
                <span className="text-[0.6875rem] text-muted-foreground">
                  {RECORD_KIND_LABEL[record.kind]}
                </span>
              </Link>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** What a recent record is, as the header's menu and the home's list call it. */
export const RECORD_KIND_LABEL: Record<IRecordVisit['kind'], string> = {
  person: 'Person',
  team: 'Team',
  firm: 'Firm',
  org: 'Organization',
  'register-firm': 'Firm in the SEC ADV register',
  response: 'Response',
  account: 'Account'
};

